import React, { useEffect, useState, useCallback } from 'react';
import * as api from '../../api';
import { useToast } from '../UI';
import MRRequestTable from './MRRequestTable';
import MRDetailsView from './MRDetailsView';
import MREditPage from './MREditPage';

// ─── Recall Modal ─────────────────────────────────────────────────────────────
function RecallModal({ row, onConfirm, onCancel, busy }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: '28px 32px',
        maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,.25)',
      }}>
        <div style={{ fontSize: 22, marginBottom: 8 }}>⚠️</div>
        <h3 style={{ margin: '0 0 10px', fontSize: 17, color: '#1e3a8a' }}>
          Recall {row.request_ref}?
        </h3>
        <p style={{ margin: '0 0 20px', fontSize: 13.5, color: '#475569', lineHeight: 1.6 }}>
          This will reverse the approval and return the request to{' '}
          <strong>Pending Approval</strong> in the Management Approval Queue.
          The approval history will be preserved but the final step reopened.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={busy} style={{ padding: '8px 20px', borderRadius: 8, border: '1.5px solid #cbd5e1', background: '#f8fafc', color: '#475569', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: busy ? '#94a3b8' : '#dc2626', color: '#fff', cursor: busy ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13 }}>
            {busy ? 'Recalling…' : 'Yes, Recall'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function MRApprovedList({ onOpen }) {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [viewRow, setViewRow]     = useState(null);
  const [editRow, setEditRow]     = useState(null);
  const [recallRow, setRecallRow] = useState(null);
  const [recallBusy, setRecallBusy] = useState(false);
  const toast = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api.listMaterialRequests({ status: 'approved', limit: 50 })
      .then(r => setRows(r?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRecall = async () => {
    if (!recallRow) return;
    setRecallBusy(true);
    try {
      const res = await api.recallMR(recallRow.request_id);
      const msg = res?.data?.message || res?.message || `${recallRow.request_ref} recalled.`;
      toast.show(msg, 'success');
      setRecallRow(null);
      load();
    } catch (e) {
      toast.show(e?.response?.data?.detail || 'Recall failed.', 'error');
    } finally {
      setRecallBusy(false);
    }
  };

  if (viewRow) {
    return <MRDetailsView row={viewRow} onBack={() => setViewRow(null)} />;
  }

  if (editRow) {
    return (
      <MREditPage
        row={editRow}
        onBack={() => setEditRow(null)}
        onSaved={() => { setEditRow(null); load(); }}
      />
    );
  }

  return (
    <>
      {recallRow && (
        <RecallModal
          row={recallRow}
          busy={recallBusy}
          onConfirm={handleRecall}
          onCancel={() => setRecallRow(null)}
        />
      )}
      <div>
        <h2 style={{ margin: '0 0 16px', fontWeight: 700, fontSize: 18 }}>Approved Requests</h2>
        <MRRequestTable
          rows={rows}
          loading={loading}
          onOpen={(id) => setEditRow(rows.find(r => r.request_id === id))}
          onView={(row) => setViewRow(row)}
          onRecall={(row) => setRecallRow(row)}
          emptyMsg="No approved requests yet."
        />
      </div>
    </>
  );
}
